/**
 * Analytics V1 API Client
 * Клиент для нового модуля modouls/ANALYTICS (порт 8072)
 * Базовый URL: /api/analytics/v1 (через Nginx proxy)
 */
import axios from 'axios';
import {
  trySurfaceShellHttpError,
  getApiErrorToastBody,
} from '../utils/shellHttpErrorSurface';
import { apiRejectPayload } from '../utils/apiRejectPayload';

const IS_DEV = process.env.NODE_ENV === 'development';

const BASE = '/api/analytics/v1';

const client = axios.create({ baseURL: BASE, timeout: 30000 });

// Прокидываем JWT из localStorage
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (r) => r.data,
  (err) => {
    const fullUrl = err.config?.baseURL && err.config?.url
      ? `${err.config.baseURL}${err.config.url}`
      : err.config?.url || '';
    trySurfaceShellHttpError(err, { requestUrl: fullUrl });
    return Promise.reject(new Error(getApiErrorToastBody(err)));
  }
);

// --- Dashboard ---
export const getDashboardOverview = (params = {}) =>
  client.get('/dashboard', { params });

export const getScorecard = () =>
  client.get('/scorecard');

// --- Products ---
export const getTopProducts = (params = {}) =>
  client.get('/products/top', { params });

export const getAbcXyz = (params = {}) =>
  client.get('/products/abc-xyz', { params });

export const getAbcXyzSummary = () =>
  client.get('/products/abc-xyz/summary');

export const getRecommendations = (productId, limit = 10) =>
  client.get('/products/recommendations', { params: { product_id: productId, limit } });

export const getPriceHistory = (productId, days = 90) =>
  client.get('/products/price-history', { params: { product_id: productId, days } });

// --- Financial (агрегированный: revenue, forecast, suppliers) ---
export const getFinancialSummary = (params = {}) =>
  client.get('/financial/summary', { params });

export const getRevenueForecast = (days = 30) =>
  client.get('/financial/forecast', { params: { days } });

export const getSupplierFinancials = () =>
  client.get('/financial/suppliers');

// --- Finance Tab (детальный: P&L, расходы, cashflow, маржа, долги, налоги) ---
// Данные из Finance backend через HMAC-клиент finance-client.js
export const getFinanceOverview = (params = {}) =>
  client.get('/finance/overview', { params });

export const getFinancePL = (params = {}) =>
  client.get('/finance/pl', { params });

export const getFinanceExpenses = (params = {}) =>
  client.get('/finance/expenses', { params });

export const getFinanceCashflow = (params = {}) =>
  client.get('/finance/cashflow', { params });

export const getFinanceMargin = (params = {}) =>
  client.get('/finance/margin', { params });

export const getFinanceDebts = () =>
  client.get('/finance/debts');

export const getFinanceTaxes = (params = {}) =>
  client.get('/finance/taxes', { params });

// --- Clients ---
export const getClientsOverview = (params = {}) =>
  client.get('/clients/overview', { params });

export const getCohortAnalysis = (months = 12) =>
  client.get('/clients/cohorts', { params: { months } });

export const getClientLTV = (months = 12) =>
  client.get('/clients/ltv', { params: { months } });

// --- Funnel ---
export const getFunnelData = (params = {}) =>
  client.get('/funnel', { params });

export const trackEvent = (event) =>
  client.post('/events', event).catch((err) => {
    if (IS_DEV) {
      console.warn('[Аналитика] не удалось отправить событие:', apiRejectPayload(err).message);
    }
  });

// --- Anomalies ---
export const getAnomalies = (days = 30) =>
  client.get('/anomalies', { params: { days } });

// --- Map ---
export const getMapData = (params = {}) =>
  client.get('/map', { params });

export const getRegionData = (params = {}) =>
  client.get('/map/regions', { params });

// --- Export ---
async function downloadExportBlob(url, filename) {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const contentType = response.headers.get('content-type') || '';
  let payload;
  if (contentType.includes('application/json')) {
    payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Не удалось экспортировать отчёт аналитики.');
    }
    const blob = new Blob([JSON.stringify(payload.data ?? payload, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    triggerDownload(blob, filename);
    return payload;
  }
  if (!response.ok) {
    throw new Error('Не удалось экспортировать отчёт аналитики.');
  }
  const blob = await response.blob();
  triggerDownload(blob, filename);
  return blob;
}

function triggerDownload(blob, filename) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export const exportReport = async (params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value != null && value !== '')
  );
  const query = new URLSearchParams(cleanParams).toString();
  const url = `${BASE}/export?${query}`;
  const report = cleanParams.report || 'sales';
  const format = cleanParams.format || 'csv';
  const ext = format === 'json' ? 'json' : 'csv';
  const filename = `analytics_${report}.${ext}`;
  return downloadExportBlob(url, filename);
};

// --- Admin ---
export const getAnalyticsSettings = () =>
  client.get('/admin/settings');

export const updateAnalyticsSettings = (settings) =>
  client.put('/admin/settings', settings);

export const triggerRecalculate = (params = {}) =>
  client.post('/admin/recalculate', params);

export const clearAnalyticsCache = () =>
  client.delete('/admin/cache');

export const getPlatformStats = () =>
  client.get('/admin/platform-stats');

export const getScheduledJobs = () =>
  client.get('/admin/jobs');

const analyticsV1 = {
  getDashboardOverview, getScorecard,
  getTopProducts, getAbcXyz, getAbcXyzSummary, getRecommendations, getPriceHistory,
  getFinancialSummary, getRevenueForecast, getSupplierFinancials,
  getFinanceOverview, getFinancePL, getFinanceExpenses, getFinanceCashflow,
  getFinanceMargin, getFinanceDebts, getFinanceTaxes,
  getClientsOverview, getCohortAnalysis, getClientLTV,
  getFunnelData, trackEvent,
  getAnomalies,
  getMapData, getRegionData,
  exportReport,
  getAnalyticsSettings, updateAnalyticsSettings,
  triggerRecalculate, clearAnalyticsCache, getPlatformStats, getScheduledJobs,
};

export default analyticsV1;

// Technical accepted original cut, no client company override.
export const getOrderProjectionStatus = (signal) => client.get('/projection/status',{signal});
